import styled from 'styled-components';
import { useState } from 'react';
import { Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import { Link } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import SettingsTable, {
	SettingsEmptyState,
} from 'packages/kakapo-ui-kit/components/common/SettingsTable';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import LearningSubjectEdit from './LearningSubjectEdit';

const AnchorElement = styled(Link)`
	color: ${(props) => props.theme.colors.primary};
	text-decoration: underline;
`;

export default function LearningSubjects() {
	const { t } = useTranslation();

	const [adding, setAdding] = useState(false);
	const [showArchived, setShowArchived] = useState(false);

	const queries = {
		active: graphql.useLearningSubjectsQuery({ variables: { first: 1000 }}),
		archive: graphql.useLearningSubjectsLazyQuery({ variables: { isArchived: true, first: 1000 } }),
	};

	const learningSubjects = queries.active.data?.learningSubjects;
	const archivedLearningSubjects = queries.archive[1]?.data?.learningSubjects;

	const onRefresh = async () => {
		await queries.active.refetch();
		await queries.archive[1]?.refetch?.();
		setAdding(false);
	};

	return (
		<>
			<Flex flexDirection="column" mb={4}>
				<SettingsTable
					title={t('common.settings.learning.learningSubjects.title')}
					description={t('common.settings.learning.learningSubjects.description')}
					toolbar={
						<Button size={ButtonSize.Medium} onClick={() => setAdding(true)}>
							{t('common.add')}
						</Button>
					}
				>
					{learningSubjects ? (
						learningSubjects.edges.length > 0 || adding ? (
							<>
								{learningSubjects.edges.map(({ node }) => (
									<LearningSubjectEdit
										key={node.id}
										learningSubject={node as graphql.LearningSubject}
										onRefresh={onRefresh}
									/>
								))}
								{adding && <LearningSubjectEdit onRefresh={onRefresh} />}
							</>
						) : (
							<SettingsEmptyState>
								{t('common.settings.learning.learningSubjects.empty')}
							</SettingsEmptyState>
						)
					) : (
						<Spinner isLoading />
					)}
				</SettingsTable>
			</Flex>

			{showArchived ? (
				<Flex flexDirection="column" mb={4}>
					<SettingsTable
						title={t('common.settings.learning.learningSubjects.archived.title')}
						description={t('common.settings.learning.learningSubjects.archived.description')}
					>
						{archivedLearningSubjects ? (
							archivedLearningSubjects.edges.length > 0 || adding ? (
								<>
									{archivedLearningSubjects.edges.map(({ node }) => (
										<LearningSubjectEdit
											key={node.id}
											learningSubject={node as graphql.LearningSubject}
											onRefresh={onRefresh}
										/>
									))}
									{adding && <LearningSubjectEdit onRefresh={onRefresh} />}
								</>
							) : (
								<SettingsEmptyState>
									{t('common.settings.learning.learningSubjects.archived.empty')}
								</SettingsEmptyState>
							)
						) : (
							<Spinner isLoading />
						)}
					</SettingsTable>
				</Flex>
			) : (
				<Flex px={3}>
					<AnchorElement
						to="/settings/learning/subjects"
						onClick={() => {
							setShowArchived(true);
							queries.archive[0]();
						}}
					>
						<ParagraphSmall>
							{t('common.settings.learning.learningSubjects.archived.button.title')}
						</ParagraphSmall>
					</AnchorElement>
				</Flex>
			)}
		</>
	);
}
